import { useUpdateUserData } from '@/api/user.api';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import {
  ReceiverProfileFormData,
  ReceiverProfileSchema,
} from '@/types/profile/ProfileFormData';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import StyledContainer from '../styled/StyledContainer';
import StyledFormHeader from '../styled/StyledFormHeader';
import StyledButton from '../styled/StyledButton';
import { CONTACT_EMAIL } from '@/constants/ContactEmail';
import HorizontalLine from '../ui/HorizontalLine';
import MailIcon from '@/assets/icons/mail-01.svg?react';
import SaveIcon from '@/assets/icons/save-02.svg?react';
import EditIcon from '@/assets/icons/edit-01.svg?react';
import { getFileKeyFromUrl } from '@/utils/s3/getFileKeyFromUrl';
import { CURRENCIES } from '../../../../shared/CountriesData';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import PlusIcon from '@/assets/icons/plus.svg?react';
import TrashIcon from '@/assets/icons/trash-02.svg?react';
import ReceiverProfileGeneralTabForm from './ReceiverProfileGeneralTabForm';
import ReceiverProfileEmployeesTab, {
  ReceiverProfileEmployeeAdditionModal,
} from './ReceiverProfileEmployeesTab';
import ModalDeletion from '../ui/ModalDeletion';
import clsx from 'clsx';
import { useDeleteEmployee, useDeleteMultipleEmployees } from '@/api/user.api';
import { LOCALES, useTranslation } from '@/i18n';
import { useLanguage } from '@/contexts/LanguageContext';
import ProfileAccountSelector from './ProfileAccountSelector';
import { toReservationEmailsFormValues } from '@/utils/profile/toReservationEmailsFormValues';
import { toastFirstFormError } from '@/utils/form/toastFirstFormError';

interface ReceiverProfileFormProps {
  userData: {
    id: string;
    email: string;
    firstname?: string;
    lastname?: string;
    company: {
      id: string;
      name?: string;
      imageUrl?: string;
      address?: string;
      city?: string;
      postalCode?: number;
      country?: string;
      currency?: string;
      website?: string;
      reservationEmails?: string[];
      contacts?: {
        id?: string;
        name?: string;
        firstName?: string;
        email?: string;
        phoneNumber?: string;
      }[];
    };
  };
  isAdmin: boolean;
}

const ReceiverProfileForm = ({
  userData,
  isAdmin,
}: ReceiverProfileFormProps) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { mutateAsync: updateUserData } = useUpdateUserData();

  const [isEditEnabled, setIsEditEnabled] = useState(false);
  const [currentTab, setCurrentTab] = useState<'profile' | 'users'>('profile');
  const [isAdditionModalOpen, setIsAdditionModalOpen] = useState(false);
  const [selectedUsersIds, setSelectedUsersIds] = useState<string[]>([]);
  const [isUserDeletionModalOpen, setIsUserDeletionModalOpen] = useState(false);

  const { toastAsync } = useToast();

  const { mutateAsync: deleteEmployee } = useDeleteEmployee();
  const { mutateAsync: deleteMultipleEmployees } = useDeleteMultipleEmployees();

  const { currentLanguage } = useLanguage();

  const defaultLanguage = useMemo(
    () => (LOCALES.includes(currentLanguage) ? currentLanguage : LOCALES[0]),
    [currentLanguage],
  );

  const {
    control,
    handleSubmit,
    formState: { dirtyFields },
    reset,
  } = useForm<ReceiverProfileFormData>({
    resolver: zodResolver(ReceiverProfileSchema),
    mode: 'onChange',
    defaultValues: {
      email: userData?.email ?? '',
      password: undefined,
      firstname: userData?.firstname ?? '',
      lastname: userData?.lastname ?? '',
      company: {
        name: userData?.company.name ?? '',
        logo: userData?.company.imageUrl ?? undefined,
        address: userData?.company.address ?? undefined,
        city: userData?.company.city ?? undefined,
        postalCode: userData?.company.postalCode ?? undefined,
        country: userData?.company.country
          ? {
              value: userData.company.country,
              label: userData.company.country,
            }
          : undefined,
        currency: userData?.company.currency
          ? {
              value: userData.company.currency,
              label: CURRENCIES.find(
                (currency) => currency.value === userData.company.currency,
              )?.label,
            }
          : undefined,
        website: userData?.company.website ?? undefined,
        reservationEmails: toReservationEmailsFormValues(
          userData?.company.reservationEmails,
        ),
        contacts: (userData?.company.contacts ?? []).map((contact) => ({
          lastName: contact.name ?? undefined,
          firstName: contact.firstName ?? undefined,
          email: contact.email ?? undefined,
          phoneNumber: contact.phoneNumber ?? undefined,
        })),
      },
      mainLanguage: {
        value: defaultLanguage,
        label: t(`common.languages.${defaultLanguage}`),
      },
    },
  });

  const submitForm: SubmitHandler<ReceiverProfileFormData> = async (data) => {
    const formData = new FormData();
    if (dirtyFields.company?.name)
      formData.append('companyName', data.company.name);
    if (dirtyFields.company?.logo && data.company.logo instanceof Blob) {
      formData.append('file', data.company.logo);
    } else if (data.company?.logo != null) {
      formData.append(
        'imageKey',
        getFileKeyFromUrl(data.company.logo as string),
      );
    }
    if (dirtyFields.company?.address && data.company.address !== undefined)
      formData.append('address', data.company.address);
    if (dirtyFields.company?.city && data.company.city !== undefined)
      formData.append('city', data.company.city);
    if (
      dirtyFields.company?.postalCode &&
      data.company.postalCode !== undefined
    )
      formData.append('postalCode', data.company.postalCode.toString());
    if (dirtyFields.company?.country && data.company.country?.value)
      formData.append('country', data.company.country?.value);
    if (dirtyFields.company?.currency && data.company.currency?.value)
      formData.append('currency', data.company.currency?.value);
    if (dirtyFields.company?.website && data.company.website !== undefined)
      formData.append('website', data.company.website);
    if (dirtyFields.email) formData.append('email', data.email);
    if (dirtyFields.firstname) formData.append('firstname', data.firstname);
    if (dirtyFields.lastname) formData.append('lastname', data.lastname);
    if (dirtyFields.password && data.password)
      formData.append('password', data.password);
    if (dirtyFields.company?.reservationEmails)
      data.company.reservationEmails.forEach(({ email }) => {
        if (email) formData.append('reservationEmails[]', email);
      });
    if (dirtyFields.company?.contacts) {
      formData.append(
        'contacts',
        JSON.stringify(
          data.company.contacts.map((contact) => ({
            ...contact,
            name: contact.lastName,
            lastName: undefined,
          })),
        ),
      );
    }

    await toastAsync(updateUserData(formData), {
      success: t('pages.profile.profileUpdatedSuccess'),
    });

    setIsEditEnabled(false);
  };

  return (
    <StyledContainer>
      <StyledFormHeader
        title={t('pages.profile.myAccount')}
        subTitle={t('pages.profile.manageConnectionAndBilling')}
        sticky
      >
        <StyledButton
          title={t('pages.profile.logout')}
          variant="outlined"
          className="font-semibold gap-1 px-3 py-2"
          onClick={() => {
            logout();
          }}
        />
        <form action={`mailto:${CONTACT_EMAIL}`} method="GET" target="_blank">
          <StyledButton
            title={t('pages.profile.contactSupport')}
            variant="outlined"
            className="font-semibold gap-1 px-3 py-2"
            icon={<MailIcon className="w-5 h-5" fill="white" />}
          />
        </form>
      </StyledFormHeader>
      <ProfileAccountSelector currentUserId={userData.id} />
      <HorizontalLine />
      <Tabs defaultValue="profile">
        <div className="flex justify-between p-6">
          <TabsList>
            <TabsTrigger
              value="profile"
              className={clsx('hover:cursor-pointer', {
                hidden: !isAdmin,
              })}
              onClick={() => setCurrentTab('profile')}
            >
              {t('pages.profile.generalInfo')}
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger
                value="users"
                className="hover:cursor-pointer"
                onClick={() => setCurrentTab('users')}
              >
                {t('pages.profile.users')}
              </TabsTrigger>
            )}
          </TabsList>
          {currentTab !== 'users' ? (
            <>
              {isEditEnabled ? (
                <div className="flex flex-row gap-4">
                  <StyledButton
                    title={t('pages.profile.cancel')}
                    onClick={() => {
                      setIsEditEnabled(false);
                      reset();
                    }}
                    variant="outlined"
                    className="font-semibold gap-1 px-3 py-2"
                  />
                  <StyledButton
                    title={t('pages.profile.saveMyInfo')}
                    className="font-semibold gap-1 px-3 py-2"
                    icon={<SaveIcon className="w-5 h-5" />}
                    onClick={handleSubmit(submitForm, (errors) =>
                      toastFirstFormError(
                        errors,
                        t('common.checkRequiredFields'),
                      ),
                    )}
                  />
                </div>
              ) : (
                <StyledButton
                  title={t('pages.profile.editMyInfo')}
                  className="font-semibold gap-1 px-3 py-2"
                  icon={<EditIcon className="w-5 h-5 stroke-white" />}
                  onClick={() => setIsEditEnabled(true)}
                />
              )}
            </>
          ) : (
            <div className="flex flex-row gap-4">
              <StyledButton
                title={t('pages.profile.delete')}
                onClick={() => {
                  setIsUserDeletionModalOpen(true);
                }}
                disabled={selectedUsersIds.length === 0}
                variant="outlined"
                className="font-semibold border-error-300 text-error-600 gap-1 px-3 py-2"
                icon={<TrashIcon className="w-5 h-5 stroke-error-600" />}
              />
              <StyledButton
                title={t('pages.profile.addUser')}
                className="font-semibold gap-1 px-3 py-2"
                icon={<PlusIcon className="size-5 fill-white" />}
                onClick={() => {
                  setIsAdditionModalOpen(true);
                }}
              />
            </div>
          )}
        </div>

        <TabsContent value="profile">
          <ReceiverProfileGeneralTabForm
            control={control}
            isEditEnabled={isEditEnabled}
            isAdmin={isAdmin}
            userData={userData}
          />
        </TabsContent>
        {isAdmin && (
          <TabsContent value="users">
            <ReceiverProfileEmployeesTab setSelected={setSelectedUsersIds} />
          </TabsContent>
        )}
      </Tabs>
      {isAdmin && isAdditionModalOpen && (
        <ReceiverProfileEmployeeAdditionModal
          closeModal={() => {
            setIsAdditionModalOpen(false);
          }}
        />
      )}
      {isAdmin && isUserDeletionModalOpen && selectedUsersIds.length > 0 && (
        <ModalDeletion
          type="user"
          actionOnClick={() => {
            if (selectedUsersIds.length === 1)
              toastAsync(deleteEmployee(selectedUsersIds[0]), {
                success: t('pages.profile.userDeletedSuccess'),
              });
            else
              toastAsync(deleteMultipleEmployees(selectedUsersIds), {
                success: t('pages.profile.usersDeletedSuccess'),
              });
          }}
          closeModal={() => {
            setSelectedUsersIds([]);
            setIsUserDeletionModalOpen(false);
          }}
          isMultiple={selectedUsersIds.length > 1}
        />
      )}
    </StyledContainer>
  );
};

export default ReceiverProfileForm;
